import { useState } from "react";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import UpcomingEvents from "@/components/UpcomingEvents";
import { X } from "lucide-react";
import { breadcrumbSchema, breadcrumbsFromPath, organizationSchema } from "@/lib/structuredData";

const galleryImages = [
  "https://static.wixstatic.com/media/36a8cf_0dffbed7ce5642469090d0b3e54615b7~mv2.jpeg",
  "https://static.wixstatic.com/media/36a8cf_eaf907ed20c44b08a9bb543d3af13263~mv2.jpeg",
  "https://static.wixstatic.com/media/36a8cf_0e40986bf8574542896353ed3263f675~mv2.jpeg",
  "https://static.wixstatic.com/media/36a8cf_1b1670732f2c428db3868ff401ac7798~mv2.png",
  "https://static.wixstatic.com/media/36a8cf_dda241935aa04df191eb022178c25f07~mv2.jpeg",
  "https://static.wixstatic.com/media/36a8cf_333d052d5ca94fcba26deb80937ed9c2~mv2.jpeg",
  "https://static.wixstatic.com/media/36a8cf_bd10ed5d9d5040b7af3ad01de31281f0~mv2.jpg",
  "https://static.wixstatic.com/media/36a8cf_11d49653e1db4cf4a66d9c709be69ea9~mv2.jpg",
  "https://static.wixstatic.com/media/36a8cf_3b0335d86a284d289c96dbd125ace8f9~mv2.jpg",
];

const Events = () => {
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>Upcoming Events | Yootelco</title>
        <meta name="description" content="See upcoming events, webinars, and conferences where Yootelco will be. Connect with our team in person." />
        <link rel="canonical" href="https://yootelco.com/events" />
        <meta property="og:title" content="Upcoming Events | Yootelco" />
        <meta property="og:description" content="See upcoming events, webinars, and conferences where Yootelco will be. Connect with our team in person." />
        <meta property="og:url" content="https://yootelco.com/events" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Upcoming Events | Yootelco" />
        <meta name="twitter:description" content="See upcoming events, webinars, and conferences where Yootelco will be. Connect with our team in person." />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema(breadcrumbsFromPath("/events")))}
        </script>
      </Helmet>
      <SiteHeader />

      <section className="pb-10 pt-[120px] md:pb-12 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container mx-auto max-w-[1080px] text-center">
          <h1 className="mx-auto max-w-[860px] font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
            Events &amp; Conferences
          </h1>
          <p className="mx-auto mt-4 max-w-[620px] text-[16px] font-medium text-muted-foreground">
            We connect with industry leaders worldwide
          </p>
        </div>
      </section>

      <UpcomingEvents showSeeAll={false} />

      <section className="section-shell">
        <div className="mx-auto max-w-[1280px]">
          <div className="text-center">
            <h2 className="why-title">Conference Gallery</h2>
            <p className="mx-auto mt-3 max-w-[600px] text-[15px] text-muted-foreground">
              Photos from our events around the world
            </p>
          </div>
          <div className="gallery-grid mt-10">
            {galleryImages.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => setLightbox(src)}
                className="block w-full overflow-hidden rounded-none transition-opacity duration-300 hover:opacity-90"
              >
                <img
                  src={src}
                  alt={`Conference photo ${i + 1}`}
                  loading="lazy"
                  className="block w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/85 p-4 backdrop-blur"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightbox}
            alt="Yootelco conference photo enlarged view"
            className="max-h-[90vh] max-w-[92vw] rounded-[14px] object-contain shadow-lift"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <SiteFooter />
    </main>
  );
};

export default Events;